import {
  parseCanvasPackage,
  type CanvasPackageParseResult,
} from '@littlebird/canvas';
import { queryOptions, type QueryClient } from '@tanstack/react-query';

import {
  type ArtifactRenderer,
  isOfficeRenderer,
} from './artifact-presentation';

export type ArtifactBody =
  | { outcome: 'ready'; text: string; canvas?: CanvasPackageParseResult }
  | { outcome: 'ready_bytes'; bytes: ArrayBuffer }
  | { outcome: 'too_large' };

export type ArtifactHead = { outcome: 'partial'; title?: string };

export const artifactBodyKey = (versionKey: string) =>
  ['artifact', 'body', versionKey] as const;

export const artifactUrlKey = (versionKey: string) =>
  ['artifact', 'url', versionKey] as const;

export const artifactHeadKey = (versionKey: string) =>
  ['artifact', 'head', versionKey] as const;

const CANVAS_TITLE_PATTERN = /"title"\s*:\s*"((?:[^"\\]|\\.)*)"/;

const decodeJsonString = (raw: string): string => {
  try {
    return JSON.parse(`"${raw}"`);
  } catch {
    return raw;
  }
};

const toBody = (
  text: string,
  renderer: ArtifactRenderer,
  byteCap: number
): ArtifactBody =>
  text.length > byteCap
    ? { outcome: 'too_large' }
    : {
        outcome: 'ready',
        text,
        canvas: renderer === 'canvas' ? parseCanvasPackage(text) : undefined,
      };

export const resolveArtifactUrl = async (
  resolveUrl: () => Promise<string | undefined>
) => {
  const url = await resolveUrl();
  if (!url) throw new Error('Artifact file is unavailable');
  return url;
};

export const fetchArtifactBody = async (
  resolveUrl: () => Promise<string | undefined>,
  renderer: ArtifactRenderer,
  byteCap: number,
  signal: AbortSignal
): Promise<ArtifactBody> => {
  const response = await fetch(await resolveArtifactUrl(resolveUrl), {
    signal,
  });
  if (!response.ok) throw new Error('Artifact file could not be read');
  if (isOfficeRenderer(renderer)) {
    const bytes = await response.arrayBuffer();
    return bytes.byteLength > byteCap
      ? { outcome: 'too_large' }
      : { outcome: 'ready_bytes', bytes };
  }
  return toBody(await response.text(), renderer, byteCap);
};

export const artifactBodyQueryOptions = (
  queryClient: QueryClient,
  versionKey: string,
  resolveUrl: () => Promise<string | undefined>,
  renderer: ArtifactRenderer,
  byteCap: number
) =>
  queryOptions({
    queryKey: artifactBodyKey(versionKey),
    staleTime: Infinity,
    retry: false,
    queryFn: async ({ signal }) => {
      const body = await fetchArtifactBody(
        resolveUrl,
        renderer,
        byteCap,
        signal
      );
      enforceArtifactCacheBudget(queryClient, body);
      return body;
    },
  });

export const fetchArtifactHead = async (
  resolveUrl: () => Promise<string | undefined>,
  endByte: number,
  byteCap: number,
  signal: AbortSignal
): Promise<{ partial: ArtifactHead; body?: ArtifactBody }> => {
  const response = await fetch(await resolveArtifactUrl(resolveUrl), {
    headers: { Range: `bytes=0-${endByte}` },
    signal,
  });
  if (!response.ok) throw new Error('Artifact file could not be read');
  const text = await response.text();
  if (response.status === 206) {
    const title = text.match(CANVAS_TITLE_PATTERN)?.[1];
    return {
      partial: { outcome: 'partial', title: title && decodeJsonString(title) },
    };
  }
  const body = toBody(text, 'canvas', byteCap);
  const parsedTitle =
    body.outcome === 'ready' && body.canvas?.success
      ? body.canvas.canvasPackage.title
      : undefined;
  return { partial: { outcome: 'partial', title: parsedTitle }, body };
};

const ARTIFACT_CACHE_MAX_BYTES = 32 * 1024 * 1024;
const ARTIFACT_CACHE_MAX_ENTRIES = 20;

const bodyBytes = (body: ArtifactBody) => {
  if (body.outcome === 'ready') return body.text.length * 2;
  return body.outcome === 'ready_bytes' ? body.bytes.byteLength : 0;
};

export const enforceArtifactCacheBudget = (
  queryClient: QueryClient,
  pending?: ArtifactBody
) => {
  const bodies = queryClient
    .getQueryCache()
    .findAll({ queryKey: ['artifact', 'body'] })
    .filter((query) => query.state.data !== undefined)
    .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt);

  let retainedBytes = pending ? bodyBytes(pending) : 0;
  let retainedEntries = pending ? 1 : 0;

  for (const query of bodies) {
    const size = bodyBytes(query.state.data as ArtifactBody);
    const exceedsBudget =
      retainedEntries + 1 > ARTIFACT_CACHE_MAX_ENTRIES ||
      retainedBytes + size > ARTIFACT_CACHE_MAX_BYTES;

    if (exceedsBudget && query.getObserversCount() === 0) {
      queryClient.removeQueries({ queryKey: query.queryKey, exact: true });
      continue;
    }
    retainedBytes += size;
    retainedEntries += 1;
  }
};
